import type { AxisSelection, UserRole } from '@atlas/types'
import { useEffect, useMemo, useState } from 'react'
import type { Campaign, CampaignStatus } from '@/domain/campaign.types'
import { CAMPAIGN_LIST_PAGE_SIZE } from '@/domain/campaign.types'
import type { CampaignListFilter } from '@/domain/campaign-ports'
import { useCampaignClassificationCatalog, useCampaigns, useCampaignsPages } from '@/hooks/useCampaigns'
import { campaignCapabilitiesFor } from '@/lib/campaign-capabilities'

// The Campaign list/filter container's data-composition layer — the sole
// consumer of `useCampaigns`/`useCampaignClassificationCatalog` for the list
// screen. `pages/Campaigns/index.tsx` renders from what this hook returns; no
// presentational component calls a hook or the resolver directly. Status
// selection is held in a field separate from `classificationSelection`, never
// merged into the Classification `AxisSelection` map.

function toggleAxisValue(selection: AxisSelection, axisCode: string, valueCode: string): AxisSelection {
	const current = selection[axisCode] ?? []
	const nextValues = current.includes(valueCode)
		? current.filter((value) => value !== valueCode)
		: [...current, valueCode]
	const next = { ...selection, [axisCode]: nextValues }
	if (nextValues.length === 0) delete next[axisCode]
	return next
}

function toggleStatus(selected: readonly CampaignStatus[], status: CampaignStatus): CampaignStatus[] {
	return selected.includes(status) ? selected.filter((value) => value !== status) : [...selected, status]
}

/** Owns the list/filter-rail's local selection state and its callbacks — split out to keep the container hook short. */
function useCampaignFilterSelectionState() {
	const [statusSelection, setStatusSelection] = useState<CampaignStatus[]>([])
	const [classificationSelection, setClassificationSelection] = useState<AxisSelection>({})
	const [shownAxisCodes, setShownAxisCodes] = useState<string[]>([])

	function onRemoveAxis(axisCode: string) {
		setShownAxisCodes((current) => current.filter((code) => code !== axisCode))
		setClassificationSelection((current) => {
			const next = { ...current }
			delete next[axisCode]
			return next
		})
	}

	return {
		statusSelection,
		classificationSelection,
		shownAxisCodes,
		onToggleStatus: (status: CampaignStatus) => setStatusSelection((current) => toggleStatus(current, status)),
		onToggleClassification: (axisCode: string, valueCode: string) =>
			setClassificationSelection((current) => toggleAxisValue(current, axisCode, valueCode)),
		onAddAxis: (axisCode: string) =>
			setShownAxisCodes((current) => (current.includes(axisCode) ? current : [...current, axisCode])),
		onRemoveAxis,
	}
}

/** Merge each page's results in request order, deduplicated by `campaignId` — never displaying a Campaign twice. */
function mergePages(pages: readonly { data?: Campaign[] }[]): Campaign[] {
	const seen = new Set<string>()
	const merged: Campaign[] = []
	for (const p of pages) {
		for (const campaign of p.data ?? []) {
			if (seen.has(campaign.campaignId)) continue
			seen.add(campaign.campaignId)
			merged.push(campaign)
		}
	}
	return merged
}

/**
 * Owns page accumulation, the in-flight load-more guard, and the terminal
 * conditions — split out to keep the container hook short. One page-count
 * counter per filter selection: reset to page 1 (and the accumulation
 * cleared, since `campaigns` is always recomputed from the live per-page
 * query cache rather than hand-appended) whenever Status or Classification
 * changes. No sorting control exists today, so no sort reset.
 */
function useCampaignListPagination(filter: CampaignListFilter) {
	const [pageCount, setPageCount] = useState(1)
	const filterSignature = `${(filter.status ?? []).join(',')}|${JSON.stringify(filter.classification)}`
	useEffect(() => {
		setPageCount(1)
	}, [filterSignature])

	const pageRequests = useMemo(
		() => Array.from({ length: pageCount }, (_, i) => ({ page: i + 1, limit: CAMPAIGN_LIST_PAGE_SIZE })),
		[pageCount],
	)
	const pages = useCampaignsPages(filter, pageRequests)

	// Always defined: `pageRequests` (and so `pages`, one query per request) has
	// length `pageCount`, which never drops below 1.
	const firstPage = pages[0]
	const lastPage = pages[pages.length - 1]
	const campaigns = useMemo(() => mergePages(pages), [pages])

	const isLoading = firstPage.isLoading
	const isError = firstPage.isError
	// Distinct from `isLoading`: true only once ≥1 load-more activation is in
	// flight, so accumulated rows stay rendered instead of being replaced by the
	// initial full-list loading state.
	const isLoadingMore = pageCount > 1 && lastPage.isLoading
	const canLoadMore =
		!isLoading && !isLoadingMore && !lastPage.isError && (lastPage.data?.length ?? 0) === CAMPAIGN_LIST_PAGE_SIZE

	function onLoadMore() {
		// Guards the in-flight case itself (not just via the rendered control being
		// disabled) so a repeated activation while a page is pending issues no
		// duplicate request, and the same page is never requested twice.
		if (!canLoadMore) return
		setPageCount((n) => n + 1)
	}

	return { campaigns, isLoading, isError, isLoadingMore, canLoadMore, onLoadMore }
}

export function useCampaignListContainer(role: UserRole | string) {
	const selection = useCampaignFilterSelectionState()
	const catalog = useCampaignClassificationCatalog()

	const filter: CampaignListFilter = useMemo(
		() => ({
			status: selection.statusSelection.length > 0 ? selection.statusSelection : undefined,
			classification: selection.classificationSelection,
		}),
		[selection.statusSelection, selection.classificationSelection],
	)

	const pagination = useCampaignListPagination(filter)
	// The existence probe never uses CAMPAIGN_LIST_PAGE_SIZE — it only needs to know whether ≥1 Campaign exists.
	const unfiltered = useCampaigns({}, { page: 1, limit: 1 })
	const capabilities = useMemo(() => campaignCapabilitiesFor(role), [role])

	const addable = catalog.data?.addable ?? []
	const addableVariables = addable.filter((variable) => !selection.shownAxisCodes.includes(variable.axisCode))
	const shownVariables = addable.filter((variable) => selection.shownAxisCodes.includes(variable.axisCode))

	return {
		...selection,
		...pagination,
		count: pagination.campaigns.length,
		hasAnyCampaignsAtAll: (unfiltered.data?.length ?? 0) > 0 || unfiltered.isLoading,
		capabilities,
		objective: catalog.data?.objective,
		geography: catalog.data?.geography,
		shownVariables,
		addableVariables,
	}
}
